import { ethers } from "ethers";

const GAS_ORACLE = "0x420000000000000000000000000000000000000F";
const ORACLE = new ethers.Interface([
  "function getL1FeeUpperBound(uint256 unsignedTxSize) view returns (uint256)",
  "function getOperatorFee(uint256 gasUsed) view returns (uint256)",
]);
export const DEPOSIT_MAX_QUOTE_AGE_MS = 120_000;
const conservative = (value: bigint) => (value * 125n + 99n) / 100n;
// The public dry run has no spendable wallet note. Budget for the additional
// nullifier, funding-context and merge-verifier work without proving/disclosing
// a real spend during an estimate. This is an allowance, not an exact gas bill;
// the complete merge is simulated and checked against it before signing.
const MERGE_GAS_ALLOWANCE = 500_000n;
const MERGE_CALLDATA_ALLOWANCE = 512;

export interface NativeDepositMaxQuote {
  account: string;
  pool: string;
  chainId: bigint;
  balance: bigint;
  amount: bigint;
  protocolFee: bigint;
  gasEstimate: bigint;
  gasLimit: bigint;
  maxFeePerGas: bigint;
  maxPriorityFeePerGas: bigint;
  networkFee: bigint;
  quotedAt: number;
}

async function additionalNetworkFee(
  provider: ethers.Provider,
  transaction: ethers.TransactionRequest,
  extraBytes = 0,
): Promise<bigint> {
  // Only Base uses this OP Stack oracle. Chikyu charges EVM execution gas in
  // DOGE and has no contract at this predeploy address. Never swallow a Base
  // oracle failure as zero fees.
  const chainId = BigInt(transaction.chainId!);
  if (chainId === 6281971n) return 0n;
  if (chainId !== 8453n && chainId !== 84532n)
    throw new Error("MAX fee estimation is not configured for this network.");
  const { from: _from, ...unsigned } = transaction;
  const size = ethers.dataLength(
    ethers.Transaction.from(unsigned as any).unsignedSerialized,
  );
  const read = async (method: string, value: bigint | number) => {
    const raw = await provider.call({
      to: GAS_ORACLE,
      data: ORACLE.encodeFunctionData(method, [value]),
    });
    return BigInt(ORACLE.decodeFunctionResult(method, raw)[0]);
  };
  const [l1Fee, operatorFee] = await Promise.all([
    read("getL1FeeUpperBound", size + extraBytes),
    read("getOperatorFee", BigInt(transaction.gasLimit!)),
  ]);
  return l1Fee + operatorFee;
}

/** Runs a real, proof-valid deposit through eth_estimateGas. Never broadcasts. */
export async function quoteNativeDepositMax(
  provider: ethers.Provider,
  simulation: {
    from: string;
    to: string;
    data: string;
    value: bigint;
    chainId: bigint;
  },
): Promise<NativeDepositMaxQuote> {
  if ((await provider.getNetwork()).chainId !== simulation.chainId)
    throw new Error(
      "Network changed. Select the deposit network and try MAX again.",
    );
  const [gasEstimate, fee, balance, nonce] = await Promise.all([
    provider.estimateGas(simulation),
    provider.getFeeData(),
    provider.getBalance(simulation.from, "pending"),
    provider.getTransactionCount(simulation.from, "pending"),
  ]);
  if (
    gasEstimate <= 0n ||
    !fee.maxFeePerGas ||
    fee.maxPriorityFeePerGas === null
  )
    throw new Error("Live network fee estimate unavailable. Try MAX again.");
  const gasLimit = conservative(gasEstimate + MERGE_GAS_ALLOWANCE);
  // Use the full balance for serialized-size accounting; MAX can only be smaller.
  const extra = await additionalNetworkFee(
    provider,
    {
      ...simulation,
      value: balance,
      nonce,
      type: 2,
      gasLimit,
      maxFeePerGas: fee.maxFeePerGas,
      maxPriorityFeePerGas: fee.maxPriorityFeePerGas,
    },
    MERGE_CALLDATA_ALLOWANCE,
  );
  const networkFee = gasLimit * fee.maxFeePerGas + conservative(extra);
  // The fresh pool's depositEth credits msg.value in full; it charges no protocol fee.
  const protocolFee = 0n;
  const amount = balance - protocolFee - networkFee;
  if (amount <= 0n)
    throw new Error(
      `Not enough ${simulation.chainId === 6281971n ? "DOGE" : "ETH"} to cover the estimated network fee.`,
    );
  return Object.freeze({
    account: simulation.from,
    pool: simulation.to,
    chainId: simulation.chainId,
    balance,
    amount,
    protocolFee,
    gasEstimate,
    gasLimit,
    maxFeePerGas: fee.maxFeePerGas,
    maxPriorityFeePerGas: fee.maxPriorityFeePerGas,
    networkFee,
    quotedAt: Date.now(),
  });
}

export function assertDepositMaxMatches(
  quote: NativeDepositMaxQuote,
  transaction: { from: string; to: string; chainId: bigint; value: bigint },
): void {
  if (
    quote.account.toLowerCase() !== transaction.from.toLowerCase() ||
    quote.pool.toLowerCase() !== transaction.to.toLowerCase() ||
    quote.chainId !== transaction.chainId ||
    quote.amount !== transaction.value
  )
    throw new Error(
      "Deposit details changed. Select MAX again before continuing.",
    );
}

/** Verify the final proof's transaction fits the reviewed allowance; never change its amount. */
export async function validateDepositMaxSubmission(
  provider: ethers.Provider,
  transaction: {
    from: string;
    to: string;
    chainId: bigint;
    value: bigint;
    data: string;
  },
  quote: NativeDepositMaxQuote,
): Promise<ethers.TransactionRequest> {
  assertDepositMaxMatches(quote, transaction);
  if ((await provider.getNetwork()).chainId !== quote.chainId)
    throw new Error("Deposit network changed. Select MAX again.");
  const [gas, balance, block, nonce] = await Promise.all([
    provider.estimateGas(transaction),
    provider.getBalance(transaction.from, "pending"),
    provider.getBlock("latest"),
    provider.getTransactionCount(transaction.from, "pending"),
  ]);
  const pricedTransaction = {
    ...transaction,
    type: 2,
    gasLimit: quote.gasLimit,
    maxFeePerGas: quote.maxFeePerGas,
    maxPriorityFeePerGas: quote.maxPriorityFeePerGas,
  };
  const extra = await additionalNetworkFee(provider, {
    ...pricedTransaction,
    nonce,
  });
  if (
    gas > quote.gasLimit ||
    block?.baseFeePerGas == null ||
    block.baseFeePerGas + quote.maxPriorityFeePerGas > quote.maxFeePerGas ||
    quote.gasLimit * quote.maxFeePerGas + extra > quote.networkFee ||
    transaction.value + quote.protocolFee + quote.networkFee > balance
  )
    throw new Error(
      "The balance or network fee has changed. Select MAX again to review the updated amount.",
    );
  return pricedTransaction;
}
